import React, { FC, useEffect, useMemo, useState } from "react";
import { AppDataContext } from "./AppDataContext";
import { Feed } from "../helpers/feed/Feed";
import { getGalleryMedia, xhr } from "../helpers/xhr";
import { groupWorkCompletedMedia, weightSorter } from "../helpers/sortUtils";
import type { IContextData } from "../types/Context";
import type { GalleryItem, GalleryMedia } from "../types/galleryTypes";
import type { Group } from "../components/services/gallery/WorkCompleted";

const EMPTY_GALLERY = {
  workCompleted: [],
  videos: [],
  playgrounds: [],
  equipmentRepairs: [],
  outdoorSports: [],
};

const mapGalleryData = (data: GalleryMedia) => {
  const workCompleted: Group[] = groupWorkCompletedMedia(data.workCompleted);
  const playgrounds: GalleryItem[] = weightSorter(data.playgrounds);
  const outdoorSports: GalleryItem[] = weightSorter(data.outdoorSports);
  const equipmentRepairs: GalleryItem[] = weightSorter(data.equipmentRepairs);
  const videos: GalleryItem[] = weightSorter(data.videos);

  return {
    playgrounds,
    outdoorSports,
    videos,
    workCompleted,
    equipmentRepairs,
  };
};

export const AppDataProvider: FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [contextData, setContextData] = useState<IContextData>({
    isBootstrapped: false,
    feedStatus: "loading",
    galleryStatus: "loading",
    gallery: EMPTY_GALLERY,
    recentFeed: [],
    homepageFeed: [],
  });

  useEffect(() => {
    let isMounted = true;
    const feedService = new Feed(xhr);

    feedService
      .getPosts()
      .then((posts) => {
        if (!isMounted) return;
        setContextData((prev) => ({
          ...prev,
          feedStatus: "success",
          recentFeed: feedService.slicePosts(posts, 20),
          homepageFeed: feedService.slicePosts(posts, 4),
        }));
      })
      .catch((error) => {
        console.error("Error occurred while loading feed", error);
        if (!isMounted) return;
        setContextData((prev) => ({ ...prev, feedStatus: "error" }));
      });

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    getGalleryMedia()
      .then((galleryPayload) => {
        if (!isMounted) return;
        setContextData((prev) => ({
          ...prev,
          galleryStatus: "success",
          gallery: mapGalleryData(galleryPayload),
        }));
      })
      .catch((error) => {
        console.error("Error occurred while loading gallery", error);
        if (!isMounted) return;
        setContextData((prev) => ({ ...prev, galleryStatus: "error" }));
      });

    return () => {
      isMounted = false;
    };
  }, []);

  const value = useMemo<IContextData>(
    () => ({
      ...contextData,
      isBootstrapped:
        contextData.feedStatus !== "loading" &&
        contextData.galleryStatus !== "loading",
    }),
    [contextData],
  );

  return <AppDataContext contextData={value}>{children}</AppDataContext>;
};
